import React from "react";
import {
  Share2,
  Facebook,
  Linkedin,
  MessageCircle,
  Mail,
  Copy,
  X,
} from "lucide-react";

const ShareModal = ({
  show,
  currentDate,
  monthNames,
  language,
  t,
  styles,
  screenSize,
  theme,
  onClose,
  sendNotification,
  createCalendarCanvas,
  handleButtonHover,
}) => {
  if (!show) return null;

  const shareOptions = [
    { id: "facebook", name: t.facebook, icon: Facebook, color: "#2e7cba" },
    { id: "linkedin", name: t.linkedin, icon: Linkedin, color: "#0077b5" },
    { id: "whatsapp", name: t.whatsApp, icon: MessageCircle, color: "#25d366" },
    {
      id: "whatsapp_share",
      name: language === "ar" ? "مشاركة واتساب (صورة)" : "WhatsApp (Image)",
      icon: MessageCircle,
      color: "#25d366",
    },
    { id: "email", name: t.email, icon: Mail, color: "#ea4335" },
  ];

  const shareToSocialMedia = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(
      language === "ar"
        ? `تحقق من تقويمي لـ ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
        : `Check out my calendar for ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    );

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${text} ${url}`,
      email: `mailto:?subject=${text}&body=${url}`,
    };

    if (platform === "whatsapp_share") {
      (async () => {
        try {
          const canvas = await createCalendarCanvas();
          const blob = await new Promise((res) =>
            canvas.toBlob(res, "image/png")
          );
          const fileName = `Calendar-${
            monthNames[currentDate.getMonth()]
          }-${currentDate.getFullYear()}.png`;
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName, text: "Shared calendar" });
          } else if (navigator.share) {
            const url = canvas.toDataURL("image/png");
            await navigator.share({
              title: fileName,
              text: language === "ar" ? "مشاركة التقويم" : "Sharing calendar",
              url,
            });
          } else {
            const text = encodeURIComponent(
              language === "ar"
                ? `ألقِ نظرة على تقويمي ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                : `Check my calendar ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
            );
            window.open(`https://wa.me/?text=${text}`, "_blank");
          }
        } catch (err) {
          console.error("WhatsApp share failed", err);
          sendNotification({
            title: language === "ar" ? "خطأ" : "Error",
            message:
              language === "ar"
                ? "فشل المشاركة عبر واتساب"
                : "Failed to share via WhatsApp",
            type: "error",
          });
        }
      })();
      onClose();
      return;
    }

    if (urls[platform]) {
      window.open(urls[platform], "_blank", "width=600,height=400");
    }
  };

  const copyCalendarLink = () => {
    navigator.clipboard.writeText(window.location.href);
    sendNotification({
      title: t.linkCopied,
      message: t.linkCopiedMessage,
      type: "success",
    });
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContainer}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitleSection}>
            <div style={styles.modalIcon}>
              <Share2 size={screenSize.isMobile ? 24 : 28} />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.shareCalendar}</h2>
              <p style={styles.modalSubtitle}>{t.shareWith}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <X size={screenSize.isMobile ? 20 : 24} />
          </button>
        </div>

        <div style={styles.formContainer}>
          <div style={{ marginBottom: screenSize.isMobile ? "16px" : "24px" }}>
            <label style={styles.formLabel}>{t.shareVia}</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: screenSize.isMobile
                  ? "repeat(2, 1fr)"
                  : "repeat(auto-fit, minmax(100px, 1fr))",
                gap: screenSize.isMobile ? "12px" : "16px",
                marginBottom: screenSize.isMobile ? "16px" : "24px",
              }}
            >
              {shareOptions.map((option) => (
                <div
                  key={option.id}
                  style={{
                    padding: screenSize.isMobile ? "16px 12px" : "20px 16px",
                    borderRadius: screenSize.isMobile ? "12px" : "16px",
                    border:
                      theme === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.1)",
                    background:
                      theme === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onClick={() => shareToSocialMedia(option.id)}
                >
                  <div
                    style={{
                      width: screenSize.isMobile ? "32px" : "40px",
                      height: screenSize.isMobile ? "32px" : "40px",
                      borderRadius: screenSize.isMobile ? "10px" : "12px",
                      background: `${option.color}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: option.color,
                    }}
                  >
                    <option.icon size={screenSize.isMobile ? 16 : 20} />
                  </div>
                  <div
                    style={{
                      fontSize: screenSize.isMobile ? "11px" : "12px",
                      fontWeight: "600",
                      color: theme === "dark" ? "#fff" : "#1a1a1a",
                    }}
                  >
                    {option.name}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: screenSize.isMobile ? "16px" : "24px" }}>
            <label style={styles.formLabel}>{t.copyLink}</label>
            <div
              style={{
                display: "flex",
                gap: "12px",
                flexDirection: language === "ar" ? "row-reverse" : "row",
              }}
            >
              <input
                type="text"
                value={window.location.href}
                readOnly
                style={styles.formInput}
              />
              <button
                onClick={copyCalendarLink}
                style={styles.actionButton("default")}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                <Copy size={16} />
              </button>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              onClick={onClose}
              style={styles.actionButton("default")}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
            >
              {t.close}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
